import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  LoadingSkeleton,
  Select,
} from '@nexora/design-system';
import {
  useProductionOrder,
  useProductionOrderCosts,
  useProductionOrderPostings,
  useProductionOrders,
  type ProductionOrder,
} from './useProductionOrders';
import { formatCurrency, formatDate, formatDateTime } from '../../shared/utils/format';

/* ── Contrato (backend: production-orders.controller.ts + manufacturing.dto.ts) ─
 *
 * GET /manufacturing/orders/:id -> ProductionOrderResponseDto + materialRequirements[]
 *   materialRequirements[]: { productId, productName?, requiredQuantity, consumedQuantity,
 *                             returnedQuantity, availableStock, unit, unitCost }
 * GET /manufacturing/orders/:id/costs -> CostSummaryResponseDto (materialCost, expectedCost,
 *                                       variance, variancePercent, costPerUnit)
 * GET /manufacturing/orders/:id/postings -> Posting[] (type, quantity, unitCost, notes, createdAt)
 *
 * Tela prototipo "Ordem de Producao #1024": BOM & Disponibilidade (Componente, Qtd.
 * Necessaria, Qtd. Consumida, Estoque Disp., Status) + Analise de Custos + Ultimos
 * Apontamentos.
 *
 * Nota de fidelidade: o backend NAO publica "status" por material — a coluna Status
 * e derivada aqui: Disponivel quando estoque >= necessario, Risco caso contrario.
 */

const STATUS_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'neutral',
  PLANNED: 'info',
  RELEASED: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CLOSED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  PLANNED: 'Planejada',
  RELEASED: 'Liberada',
  IN_PROGRESS: 'Em Produção',
  COMPLETED: 'Concluída',
  CLOSED: 'Fechada',
  CANCELLED: 'Cancelada',
};

export function ProductionOrderDetailPage(): JSX.Element {
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const routeId = params['id'] ?? params['productionOrderId'];

  const [selectedId, setSelectedId] = useState<string>(routeId ?? '');
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  /* Sem rota parametrizada (router fora do escopo), a pagina oferece um seletor
     de OP — mesmo padrao de PayableWriteOffPage. */
  const { data: listData, isLoading: listLoading } = useProductionOrders({ page: 1, limit: 50 });
  const orders = useMemo(() => listData?.data ?? [], [listData]);

  const { data: order, isLoading, error, refetch } = useProductionOrder(selectedId || undefined);
  const { data: costs } = useProductionOrderCosts(selectedId || undefined);
  const { data: postings } = useProductionOrderPostings(selectedId || undefined);

  const header = useMemo(() => {
    const find = orders.find((o) => o.id === selectedId);
    return find;
  }, [orders, selectedId]);

  const materialRows = order?.materialRequirements ?? [];

  const materialStatus = (req: {
    requiredQuantity: number;
    availableStock: number;
  }): { label: string; variant: 'success' | 'error' } =>
    req.availableStock >= req.requiredQuantity
      ? { label: 'Disponível', variant: 'success' }
      : { label: 'Risco', variant: 'error' };

  const costRows = costs
    ? [
        { key: 'estimated', label: 'Custo Estimado (Matéria-prima)', value: costs.expectedCost, tone: 'default' as const },
        { key: 'actual', label: 'Custo Real (Até o momento)', value: costs.materialCost, tone: 'default' as const },
        { key: 'variance', label: 'Variação', value: costs.variance, tone: costs.variance > 0 ? ('error' as const) : ('success' as const), percent: true },
      ]
    : [];

  return (
    <div className="prod-detail-page">
      <style>{`
        .prod-detail-page { padding: 1.5rem; }
        .prod-detail-header { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1.5rem; }
        .prod-detail-table-wrap { overflow-x: auto; }
        .prod-detail-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .prod-detail-table th { text-align: left; padding: 0.5rem 0.75rem; font-weight: 600; color: #43474e; border-bottom: 1px solid #c4c6cf; background: #eceef0; white-space: nowrap; }
        .prod-detail-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #e0e3e5; }
        .prod-detail-num { text-align: right; font-variant-numeric: tabular-nums; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .prod-detail-grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 1.5rem; }
        .prod-detail-col-main { grid-column: span 12; }
        .prod-detail-col-side { grid-column: span 12; }
        .prod-detail-cost-row { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0; border-bottom: 1px solid #e0e3e5; }
        .prod-detail-cost-row:last-child { border-bottom: none; }
        .prod-detail-post { border-left: 2px solid #002045; padding-left: 0.75rem; margin-bottom: 0.75rem; }
        .prod-detail-empty { padding: 2rem 1rem; text-align: center; color: #43474e; }
        .prod-detail-error { padding: 1.5rem; text-align: center; color: #ba1a1a; }
        @media (min-width: 1280px) {
          .prod-detail-col-main { grid-column: span 8; }
          .prod-detail-col-side { grid-column: span 4; }
        }
      `}</style>

      <div className="prod-detail-header">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-xs uppercase tracking-wider">
            Produção / Ordem de Produção
          </p>
          {header ? (
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">
              Ordem de Produção {header.orderNumber}
            </h1>
          ) : (
            <h1 className="font-display-lg text-display-lg text-on-surface m-0">
              Ordem de Produção
            </h1>
          )}
          <p className="font-body-md text-body-md text-on-surface-variant mt-sm m-0">
            {order
              ? `${order.productName} | Quantidade Solicitada: ${order.plannedQuantity}`
              : 'Selecione uma ordem para ver o detalhe.'}
          </p>
        </div>
        {order && (
          <Badge variant={STATUS_VARIANT[order.status] ?? 'neutral'} size="md">
            {STATUS_LABEL[order.status] ?? order.status}
          </Badge>
        )}
      </div>

      {/* Seletor de OP — ativo apenas fora de rota parametrizada */}
      {!routeId && (
        <Card variant="outlined" padding="md" className="mb-lg">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ minWidth: '280px', flex: 1 }}>
              <Select
                label="Ordem de Produção"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">— Selecionar —</option>
                {orders.map((o: ProductionOrder) => (
                  <option key={o.id} value={o.id}>
                    {o.orderNumber} — {o.productName}
                  </option>
                ))}
              </Select>
            </div>
            {listLoading && <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando ordens...</p>}
          </div>
        </Card>
      )}

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="prod-detail-error" role="alert">
            <p>Erro ao carregar a ordem de produção</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {(error as Error).message}
            </p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      )}

      {!isLoading && !error && !order && (
        <Card padding="2rem">
          <div className="prod-detail-empty">
            {selectedId
              ? 'Ordem não encontrada.'
              : 'Nenhuma ordem selecionada. Escolha uma ordem no seletor acima.'}
          </div>
        </Card>
      )}

      {order && (
        <div className="prod-detail-grid">
          {/* BOM & Disponibilidade */}
          <div className="prod-detail-col-main">
            <Card padding="0">
              <CardHeader
                className="px-md pt-md"
                action={
                  <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                    {materialRows.length} {materialRows.length === 1 ? 'componente' : 'componentes'}
                  </span>
                }
              >
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
                  Lista de Materiais (BOM) &amp; Disponibilidade
                </h3>
              </CardHeader>
              {materialRows.length === 0 ? (
                <div className="prod-detail-empty">
                  Esta ordem ainda não tem necessidades de material registradas
                  (planeje ou libere a ordem para gerar os componentes do BOM).
                </div>
              ) : (
                <div className="prod-detail-table-wrap">
                  <table className="prod-detail-table">
                    <thead>
                      <tr>
                        <th>Componente</th>
                        <th style={{ textAlign: 'right' }}>Qtd. Necessária</th>
                        <th style={{ textAlign: 'right' }}>Qtd. Consumida</th>
                        <th style={{ textAlign: 'right' }}>Estoque Disp.</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialRows.map((req) => {
                        const st = materialStatus(req);
                        return (
                          <tr key={req.productId}>
                            <td>
                              {req.productName ?? req.productId}
                              <span className="font-label-sm text-label-sm text-on-surface-variant ml-xs">
                                ({req.unit})
                              </span>
                            </td>
                            <td className="prod-detail-num">{req.requiredQuantity}</td>
                            <td className="prod-detail-num">{req.consumedQuantity}</td>
                            <td className="prod-detail-num">{req.availableStock}</td>
                            <td>
                              <Badge variant={st.variant} size="sm">
                                {st.label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Ultimos Apontamentos */}
            <Card padding="md" className="mt-lg">
              <CardHeader>
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
                  Últimos Apontamentos
                </h3>
              </CardHeader>
              {!postings || postings.length === 0 ? (
                <div className="prod-detail-empty">
                  Nenhum apontamento registrado até o momento.
                </div>
              ) : (
                <div>
                  {postings.slice(0, 6).map((p) => (
                    <div key={p.id} className="prod-detail-post">
                      <p className="font-body-sm text-body-sm text-on-surface m-0">
                        {p.type === 'CONSUMPTION'
                          ? `Consumo de ${p.quantity} un`
                          : p.type === 'PRODUCTION'
                            ? `Produção apontada: ${p.quantity} un`
                            : `${p.type}: ${p.quantity} un`}
                        {p.notes ? ` — ${p.notes}` : ''}
                      </p>
                      <p className="font-label-sm text-label-sm text-on-surface-variant m-0">
                        {formatDateTime(p.createdAt)} · {formatCurrency(p.totalCost)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Analise de Custos */}
          <div className="prod-detail-col-side">
            <Card padding="md">
              <CardHeader>
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
                  Análise de Custos
                </h3>
              </CardHeader>
              {!costs ? (
                <div className="prod-detail-empty">Custos indisponíveis para esta ordem.</div>
              ) : (
                <div>
                  {costRows.map((row) => (
                    <div key={row.key} className="prod-detail-cost-row">
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        {row.label}
                      </span>
                      <span
                        className={`font-label-md text-label-md ${
                          'percent' in row && row.percent
                            ? row.tone === 'error'
                              ? 'text-error'
                              : 'text-primary'
                            : 'text-on-surface'
                        }`}
                      >
                        {row.percent
                          ? `${row.value > 0 ? '+' : ''}${row.value.toFixed(1)}%`
                          : formatCurrency(row.value)}
                      </span>
                    </div>
                  ))}
                  {costs.costPerUnit !== undefined && (
                    <div className="prod-detail-cost-row">
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        Custo por unidade
                      </span>
                      <span className="font-label-md text-label-md text-on-surface">
                        {formatCurrency(costs.costPerUnit)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </Card>

            {order && (
              <Card padding="md" className="mt-lg">
                <div className="flex flex-col gap-xs">
                  <div className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
                    Datas
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body-sm text-body-sm text-on-surface-variant">Início planejado</span>
                    <span className="font-label-md text-label-md text-on-surface">{formatDate(order.scheduledStart)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body-sm text-body-sm text-on-surface-variant">Fim planejado</span>
                    <span className="font-label-md text-label-md text-on-surface">{formatDate(order.scheduledEnd)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-body-sm text-body-sm text-on-surface-variant">BOM</span>
                    <span className="font-label-md text-label-md text-on-surface">
                      {order.bomId ? `v${order.bomVersion ?? ''}` : '—'}
                    </span>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}